import React from 'react';

import { Alert, Button } from '../common';

import type { DocumentCheck } from '../../api';

const TITLES: Record<string, string> = {
	NOT_A_STATEMENT: 'This file is not a processing statement',
	MISSING_PAGES: 'Pages are missing from this statement',
	UNREADABLE: 'Parts of this statement could not be read',
	PARTLY_READABLE: 'Part of this statement is hard to read',
	OTHER: 'There is a problem with this statement',
};

export const isNotAStatement = (check?: DocumentCheck | null): boolean =>
	check?.status === 'flagged' &&
	check.issues.some((issue) => issue.code === 'NOT_A_STATEMENT');

// No quote from a file that isn't a statement or is missing pages (ROC-75).
const QUOTE_BLOCKING_CODES = new Set(['NOT_A_STATEMENT', 'MISSING_PAGES']);

export const blocksQuote = (check?: DocumentCheck | null): boolean =>
	check?.status === 'flagged' &&
	check.issues.some((issue) => QUOTE_BLOCKING_CODES.has(issue.code));

export interface UploadAction {
	label: string;
	mode: 'append' | 'replace';
}

const REPLACE: UploadAction = {
	label: 'Upload the right statement',
	mode: 'replace',
};

// What the user can upload next, from every issue on the file (ROC-90).
export const uploadActions = (
	check: DocumentCheck,
	failed: boolean,
): UploadAction[] => {
	const codes = new Set(check.issues.map((issue) => issue.code));
	if (codes.has('NOT_A_STATEMENT')) return [REPLACE];
	if (codes.has('UNREADABLE'))
		return [{ label: 'Upload a clearer copy', mode: 'replace' }];
	if (codes.has('MISSING_PAGES'))
		return [
			{ label: 'Upload missing pages', mode: 'append' },
			{ label: 'Upload the complete statement', mode: 'replace' },
		];
	return failed ? [REPLACE] : [];
};

// The banner title follows the most serious issue, the same order as the buttons.
const TITLE_ORDER = [
	'NOT_A_STATEMENT',
	'UNREADABLE',
	'MISSING_PAGES',
	'PARTLY_READABLE',
];

/** The headline for the most serious issue on a flagged check. */
export const documentCheckTitle = (check: DocumentCheck): string => {
	const codes: string[] = check.issues.map((issue) => issue.code);
	const titleCode = TITLE_ORDER.find((code) => codes.includes(code));
	return TITLES[titleCode ?? codes[0]] ?? TITLES.OTHER;
};

interface DocumentCheckBannerProps {
	check?: DocumentCheck | null;
	/** The run failed, so even an issue without its own action offers a replacement. */
	failed?: boolean;
	/** Opens the upload for the banner's action; buttons are hidden without it. */
	onUpload?: (action: UploadAction) => void;
	/** v9 "Email <contact>": first when the file is not a statement, else after the first action. */
	mailAction?: React.ReactNode;
}

/** Warning for a flagged upload (ROC-72). Renders nothing when the check is ok or absent. */
const DocumentCheckBanner: React.FC<DocumentCheckBannerProps> = ({
	check,
	failed = false,
	onUpload,
	mailAction,
}) => {
	if (!check || check.status !== 'flagged' || check.issues.length === 0) {
		return null;
	}
	const variant = isNotAStatement(check) ? 'error' : 'warning';
	const title = documentCheckTitle(check);
	const actions = onUpload ? uploadActions(check, failed) : [];
	const mailFirst = Boolean(mailAction) && isNotAStatement(check);
	return (
		<Alert variant={variant} title={title}>
			<ul className="list-disc space-y-1 pl-5">
				{check.issues.map((issue, index) => (
					<li key={`${issue.code}-${index}`}>{issue.ui_message}</li>
				))}
			</ul>
			{(actions.length > 0 || mailAction) && (
				<div className="mt-3 flex flex-wrap items-center gap-3">
					{mailFirst && mailAction}
					{actions.map((action, index) => (
						<React.Fragment key={action.label}>
							<Button
								size="sm"
								variant={index === 0 && !mailFirst ? 'primary' : 'outline'}
								onClick={() => onUpload?.(action)}
							>
								{action.label}
							</Button>
							{index === 0 && !mailFirst && mailAction}
						</React.Fragment>
					))}
					{actions.length === 0 && !mailFirst && mailAction}
				</div>
			)}
		</Alert>
	);
};

export default DocumentCheckBanner;
